import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";

interface Profile {
  id: string;
  email: string;
  display_name: string | null;
  avatar_url: string | null;
  user_type: "guest" | "regular" | "premium";
  created_at: string;
  updated_at: string;
}

interface RateLimitInfo {
  used: number;
  limit: number;
  remaining: number;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  rateLimit: RateLimitInfo | null;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signUp: (email: string, password: string, displayName?: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshRateLimit: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const RATE_LIMITS = {
  guest: 5,
  regular: 50,
  premium: 500,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [rateLimit, setRateLimit] = useState<RateLimitInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("id", userId)
      .single();

    if (error) {
      console.error("Error fetching profile:", error);
      return null;
    }

    return data as Profile;
  };

  const fetchRateLimit = async (userId: string, userType: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const { count, error } = await supabase
      .from("rate_limits")
      .select("*", { count: "exact", head: true })
      .eq("user_id", userId)
      .eq("action_type", "generation")
      .gte("created_at", today.toISOString());

    if (error) {
      console.error("Error fetching rate limit:", error);
      return null;
    }

    const limit = RATE_LIMITS[userType as keyof typeof RATE_LIMITS] || RATE_LIMITS.regular;
    const used = count || 0;

    return {
      used,
      limit,
      remaining: Math.max(0, limit - used),
    };
  };

  const refreshRateLimit = async () => {
    if (user && profile) {
      const rateLimitInfo = await fetchRateLimit(user.id, profile.user_type);
      setRateLimit(rateLimitInfo);
    }
  };

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        setSession(session);
        setUser(session?.user ?? null);

        // Defer profile fetch with setTimeout
        if (session?.user) {
          setTimeout(async () => {
            const profileData = await fetchProfile(session.user.id);
            setProfile(profileData);
            if (profileData) {
              const rateLimitInfo = await fetchRateLimit(session.user.id, profileData.user_type);
              setRateLimit(rateLimitInfo);
            }
            setIsLoading(false);
          }, 0);
        } else {
          setProfile(null);
          setRateLimit(null);
          setIsLoading(false);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);

      if (session?.user) {
        const profileData = await fetchProfile(session.user.id);
        setProfile(profileData);
        if (profileData) {
          const rateLimitInfo = await fetchRateLimit(session.user.id, profileData.user_type);
          setRateLimit(rateLimitInfo);
        }
      }
      setIsLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    return { error: error as Error | null };
  };

  const signUp = async (email: string, password: string, displayName?: string) => {
    const redirectUrl = `${window.location.origin}/`;

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          display_name: displayName,
        },
      },
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        profile,
        rateLimit,
        isLoading,
        signIn,
        signUp,
        signOut,
        refreshRateLimit,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
